import { useEffect, useMemo, useState } from "react";
import { addDays, differenceInCalendarDays, format, isBefore, parseISO, startOfDay } from "date-fns";
import { DayPicker } from "react-day-picker";
import "react-day-picker/dist/style.css";
import { CalendarDays, LoaderCircle } from "lucide-react";
import { frontendApi } from "@/lib/api";

const normalizeBlock = (block) => {
  const fromValue = block?.from || block?.start_date;
  const toValue = block?.to || block?.end_date;
  if (!fromValue || !toValue) return null;

  const from = startOfDay(parseISO(fromValue));
  const to = startOfDay(parseISO(toValue));
  if (Number.isNaN(from.getTime()) || Number.isNaN(to.getTime())) return null;

  return { from, to, status: block?.status || "unavailable" };
};

const dateFallsInBlock = (date, block) => date >= block.from && date <= block.to;

const rangeCrossesBlock = (range, blocks) => {
  if (!range?.from || !range?.to) return false;

  // Checkout is exclusive, so a guest may leave on the day another booking starts.
  for (let day = startOfDay(range.from); day < startOfDay(range.to); day = addDays(day, 1)) {
    if (blocks.some((block) => dateFallsInBlock(day, block))) return true;
  }
  return false;
};

export default function BookingCalendar({ propertySlug, value, onChange }) {
  const [blocks, setBlocks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const today = useMemo(() => startOfDay(new Date()), []);

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      setLoading(true);
      setError("");
      try {
        const response = await frontendApi.propertyAvailabilityRanges(propertySlug);
        const data = response?.unwrapped ?? response?.data ?? response;
        const normalized = (data?.blocks || data?.ranges || [])
          .map(normalizeBlock)
          .filter(Boolean);
        if (!cancelled) setBlocks(normalized);
      } catch (requestError) {
        if (!cancelled) {
          setError(requestError?.apiMessage || "Availability could not be loaded. Dates will be rechecked before payment.");
          setBlocks([]);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    if (propertySlug) load();
    return () => {
      cancelled = true;
    };
  }, [propertySlug]);

  const disabled = useMemo(
    () => [{ before: today }, ...blocks.map(({ from, to }) => ({ from, to }))],
    [blocks, today],
  );

  const handleSelect = (nextRange) => {
    setError("");
    if (!nextRange?.from) {
      onChange(undefined);
      return;
    }

    if (isBefore(startOfDay(nextRange.from), today)) {
      setError("Check-in cannot be in the past.");
      return;
    }

    if (nextRange.to && differenceInCalendarDays(nextRange.to, nextRange.from) < 1) {
      setError("Checkout must be at least one night after check-in.");
      return;
    }

    if (nextRange.to && rangeCrossesBlock(nextRange, blocks)) {
      setError("That stay crosses unavailable or booked dates. Choose a range entirely before or after the blocked dates.");
      onChange({ from: nextRange.from, to: undefined });
      return;
    }

    onChange(nextRange);
  };

  return (
    <div>
      <div className="mb-4 flex items-center justify-between gap-4">
        <div>
          <p className="text-[10px] font-medium uppercase tracking-[0.18em] text-muted-foreground">Stay dates</p>
          <p className="mt-1 text-sm">
            {value?.from ? format(value.from, "MMM d, yyyy") : "Select check-in"}
            {value?.to ? ` – ${format(value.to, "MMM d, yyyy")}` : ""}
          </p>
        </div>
        {loading ? <LoaderCircle size={17} className="animate-spin text-muted-foreground" /> : <CalendarDays size={17} className="text-primary" />}
      </div>

      <div className="overflow-x-auto border border-foreground/10 p-2">
        <DayPicker
          mode="range"
          selected={value}
          onSelect={handleSelect}
          disabled={disabled}
          fromDate={today}
          numberOfMonths={1}
          showOutsideDays
          fixedWeeks
          className="mx-auto"
        />
      </div>

      <div className="mt-3 flex flex-wrap gap-x-5 gap-y-2 text-[11px] text-muted-foreground">
        <span className="inline-flex items-center gap-2"><span className="h-2.5 w-2.5 border border-foreground/20 bg-muted" /> Unavailable or booked</span>
        <span>Checkout dates are treated as departure days.</span>
      </div>

      {error && <p className="mt-3 text-sm leading-relaxed text-destructive">{error}</p>}
    </div>
  );
}
